import React, { useState, useEffect } from 'react';
import { isMobile } from '../../../shared/utils/deviceUtils';

export const Hero: React.FC = () => {
  const [currentTaglineIndex, setCurrentTaglineIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageVisible, setImageVisible] = useState(true);
  const mobile = isMobile();

  // Hero background images that sync with taglines
  const heroImages = [
    'https://images.pexels.com/photos/4553618/pexels-photo-4553618.jpeg',
    'https://images.pexels.com/photos/4577791/pexels-photo-4577791.jpeg',
    'https://images.pexels.com/photos/4577801/pexels-photo-4577801.jpeg',
    'https://images.pexels.com/photos/631317/pexels-photo-631317.jpeg',
    'https://images.pexels.com/photos/5669788/pexels-photo-5669788.jpeg',
    'https://images.pexels.com/photos/12086990/pexels-photo-12086990.jpeg',
  ];

  const taglines = [
    {
      main: 'Discover Your Perfect',
      highlight: 'Safari Adventure',
      subtitle:
        'Experience the magic of African wildlife with our curated safari experiences',
      image:
        'https://images.pexels.com/photos/4553618/pexels-photo-4553618.jpeg',
    },
    {
      main: 'Unleash Your Wild',
      highlight: 'Spirit Today',
      subtitle:
        "Journey into Tanzania's untamed wilderness and create memories that last forever",
      image:
        'https://images.pexels.com/photos/4577791/pexels-photo-4577791.jpeg',
    },
    {
      main: 'Where Dreams Meet',
      highlight: 'The Serengeti',
      subtitle:
        "Connect with nature's most spectacular wildlife in their natural habitat",
      image:
        'https://images.pexels.com/photos/4577801/pexels-photo-4577801.jpeg',
    },
    {
      main: 'Adventure Awaits in',
      highlight: 'Every Sunrise',
      subtitle:
        "Wake up to the sounds of the wild and witness Africa's incredible biodiversity",
      image: 'https://images.pexels.com/photos/631317/pexels-photo-631317.jpeg',
    },
    {
      main: 'Your Gateway to',
      highlight: 'African Magic',
      subtitle:
        'Discover hidden gems and iconic destinations with expert local guides',
      image:
        'https://images.pexels.com/photos/5669788/pexels-photo-5669788.jpeg',
    },
    {
      main: 'Beyond Ordinary',
      highlight: 'Safari Dreams',
      subtitle:
        "Luxury meets adventure in Tanzania's most breathtaking national parks",
      image:
        'https://images.pexels.com/photos/12086990/pexels-photo-12086990.jpeg',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false);
      setImageVisible(false);

      setTimeout(() => {
        setCurrentTaglineIndex(prev => (prev + 1) % taglines.length);
        setCurrentImageIndex(prev => (prev + 1) % heroImages.length);
        setIsVisible(true);
        setImageVisible(true);
      }, 500); // Half second for fade out
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [taglines.length, heroImages.length]);

  const currentTagline = taglines[currentTaglineIndex];

  return (
    <div className="relative h-[100vh] overflow-hidden">
      {/* Solid color header for navbar visibility */}
      <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-black/80 to-transparent z-10"></div>

      {/* Dynamic Background Images with Smooth Transitions */}
      <div className="absolute inset-0">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
              index === currentImageIndex && imageVisible
                ? 'opacity-100 scale-100'
                : index === currentImageIndex
                  ? 'opacity-0 scale-105'
                  : 'opacity-0 scale-95'
            }`}
          >
            <img
              src={image}
              alt={`Safari landscape ${index + 1}`}
              className="w-full h-full object-cover transition-transform duration-1000 ease-in-out"
            />
          </div>
        ))}

        {/* Enhanced gradient overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/30"></div>

        {/* Subtle animated overlay for depth */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-primary-900/20 via-transparent to-accent-coral-900/20 animate-pulse"
          style={{ animationDuration: '8s' }}
        ></div>
      </div>

      {/* Hero Content - Positioned higher to prevent cutoff */}
      <div className="relative z-10 h-full flex items-center justify-center text-center">
        <div className="max-w-6xl px-4 sm:px-6 lg:px-8 w-full">
          {/* Main Tagline with Smooth Transitions - Positioned at top */}
          <div
            className={`transition-all duration-500 ease-in-out transform ${
              isVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-4'
            }`}
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-8 leading-tight text-center">
              {currentTagline.main}
              <span className="block text-transparent bg-gradient-to-r from-accent-coral-400 via-accent-teal-400 to-primary-400 bg-clip-text animate-pulse mt-2 font-black">
                {currentTagline.highlight}
              </span>
            </h1>

            <p className="text-lg sm:text-xl md:text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed mb-8 text-center font-medium">
              {currentTagline.subtitle}
            </p>
          </div>

          {/* Promotional Badges - Bigger and more mobile-friendly */}
          <div className={`flex ${mobile ? 'flex-col' : 'flex-wrap'} items-center justify-center gap-4 sm:gap-6 mb-8`}>
            <div className="flex items-center gap-3 px-4 sm:px-6 py-3 sm:py-4 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 min-w-[200px] sm:min-w-[220px] justify-center">
              <div className="w-3 h-3 bg-accent-success-400 rounded-xl animate-pulse"></div>
              <span className="text-white/90 text-sm sm:text-base font-medium">
                15K+ Happy Travelers
              </span>
            </div>

            <div className="flex items-center gap-3 px-4 sm:px-6 py-3 sm:py-4 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 min-w-[200px] sm:min-w-[220px] justify-center">
              <div className="w-3 h-3 bg-accent-warning-400 rounded-xl animate-pulse"></div>
              <span className="text-white/90 text-sm sm:text-base font-medium">
                98% Satisfaction Rate
              </span>
            </div>

            <div className="flex items-center gap-3 px-4 sm:px-6 py-3 sm:py-4 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 min-w-[200px] sm:min-w-[220px] justify-center">
              <div className="w-3 h-3 bg-accent-coral-400 rounded-xl animate-pulse"></div>
              <span className="text-white/90 text-sm sm:text-base font-medium">
                Expert Local Guides
              </span>
            </div>
          </div>

          {/* Floating Elements for Visual Interest */}
          <div
            className="absolute top-1/4 left-1/4 w-4 h-4 bg-accent-coral-400/30 rounded-full animate-bounce"
            style={{ animationDelay: '0s', animationDuration: '3s' }}
          ></div>
          <div
            className="absolute top-1/3 right-1/4 w-3 h-3 bg-accent-teal-400/30 rounded-full animate-bounce"
            style={{ animationDelay: '1s', animationDuration: '4s' }}
          ></div>
          <div
            className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-primary-400/30 rounded-full animate-bounce"
            style={{ animationDelay: '2s', animationDuration: '5s' }}
          ></div>
        </div>
      </div>

      {/* Smooth transition indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex gap-2">
          {heroImages.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-xl transition-all duration-500 ${
                index === currentImageIndex ? 'bg-white w-8' : 'bg-white/40'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
